import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import MediaUploadStatus from 'types/MediaUploadStatus';

export type TaggedPublishers = {
  id: number;
  name: string;
  image: string | null;
};

export type ExtendedFile = {
  fileUrl: string;
  name: string;
  type: string;
  size: number;
  status: MediaUploadStatus;
  progress: number;
  attachmentId?: number;
  taggedPublishers?: TaggedPublishers[];
};

interface UploadMediaState {
  mediaItems: ExtendedFile[];
  currentUploads: ExtendedFile[];
  completedUploads: ExtendedFile[];
  uploadQueue: ExtendedFile[];
  isProcessingUploads: boolean;
  isUploadsComplete: boolean;
}

const initialState: UploadMediaState = {
  currentUploads: [],
  completedUploads: [],
  uploadQueue: [],
  mediaItems: [],
  isProcessingUploads: false,
  isUploadsComplete: false,
};

const directUploadMediaSlice = createSlice({
  name: 'uploadMedia',
  initialState,
  reducers: {
    setMediaItems(state, action: PayloadAction<ExtendedFile[]>) {
      state.mediaItems = action.payload;
    },
    setCurrentUploads(state, action: PayloadAction<ExtendedFile[]>) {
      state.currentUploads = action.payload;
    },
    updateCurrentUploads(state, action: PayloadAction<ExtendedFile>) {
      state.currentUploads = state.currentUploads.filter(
        (item) => item.fileUrl !== action.payload.fileUrl
      );
    },
    setCompletedUploads(state, action: PayloadAction<ExtendedFile>) {
      state.completedUploads.push(action.payload);
    },
    setUploadQueue(state, action: PayloadAction<ExtendedFile[]>) {
      state.uploadQueue = action.payload;
    },
    setIsProcessingUploads(state, action: PayloadAction<boolean>) {
      state.isProcessingUploads = action.payload;
    },
    setIsUploadsComplete(state, action: PayloadAction<boolean>) {
      state.isUploadsComplete = action.payload;
    },
    updateMediaItem(
      state,
      action: PayloadAction<{
        fileUrl: string;
        newStatus: MediaUploadStatus;
        progress?: number;
        attachmentId?: number;
      }>
    ) {
      const { fileUrl, newStatus, progress, attachmentId } = action.payload;
      state.mediaItems = state.mediaItems.map((item) =>
        item.fileUrl === fileUrl
          ? {
              ...item,
              status: newStatus,
              progress: progress || item.progress,
              attachmentId: attachmentId || item.attachmentId,
            }
          : item
      );
    },
    removeMediaItem(state, action: PayloadAction<string>) {
      const fileUrlToRemove = action.payload;
      state.mediaItems = state.mediaItems.filter(
        (item) => item.fileUrl !== fileUrlToRemove
      );
    },
    updateMediaItemByAttachmentId(
      state,
      action: PayloadAction<{
        attachmentId: number;
        newStatus: MediaUploadStatus;
      }>
    ) {
      const { attachmentId, newStatus } = action.payload;
      state.mediaItems = state.mediaItems.map((item) =>
        item.attachmentId === attachmentId
          ? {
              ...item,
              status: newStatus,
            }
          : item
      );
    },
    resetState() {
      return initialState;
    },
  },
});

export const {
  setMediaItems,
  setCurrentUploads,
  setCompletedUploads,
  setUploadQueue,
  setIsProcessingUploads,
  setIsUploadsComplete,
  updateCurrentUploads,
  updateMediaItem,
  updateMediaItemByAttachmentId,
  resetState,
  removeMediaItem,
} = directUploadMediaSlice.actions;

export default directUploadMediaSlice.reducer;
